// Модель и глубина размышлений чата. До первого сообщения выбор хранится черновиком и уходит
// при создании треда; у существующего треда — PATCH (глубину отправляем с паузой, чтобы
// перебор уровней стрелками не перезапускал агента на каждом шаге).
import { type ModelChoice, resolveModel } from '@autoaff/protocol';
import { toast } from '@autoaff/ui';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../api';
import { useModels } from '../models';
import { chatStore, useChat } from './store';

export interface ThreadMeta {
  id: string;
  title: string | null;
  model: string;
  effort: string | null;
  working: boolean;
  /** Итог разговора (агент подводит его после паузы). */
  summary?: { headline: string | null; text: string; at: string | null } | null;
}

export function useChatModel(threadId: string | null) {
  const models = useModels();
  const chat = useChat();
  const qc = useQueryClient();
  const meta = useQuery({
    queryKey: ['thread', threadId],
    queryFn: () => api<ThreadMeta>(`/api/threads/${threadId}`),
    enabled: threadId !== null,
  });
  const [local, setLocal] = useState<ModelChoice | null>(null);
  const pending = useRef<{ timer: ReturnType<typeof setTimeout>; run: () => void } | null>(null);
  const inflight = useRef<Promise<void> | null>(null);

  // biome-ignore lint/correctness/useExhaustiveDependencies: сбрасываем локальный выбор при смене треда
  useEffect(() => {
    setLocal(null);
    if (pending.current) clearTimeout(pending.current.timer);
    pending.current = null;
  }, [threadId]);

  const fromMeta = meta.data ? { model: meta.data.model, effort: meta.data.effort } : null;
  const fromSettings = models.data ? resolveModel(models.data.settings, 'chat') : null;
  const value: ModelChoice | null = threadId
    ? (local ?? fromMeta ?? chat.draftChoice ?? fromSettings)
    : (chat.draftChoice ?? fromSettings);

  const change = useCallback(
    (choice: ModelChoice, opts: { debounce?: boolean } = {}) => {
      if (!threadId) {
        chatStore.setDraftChoice(choice);
        return;
      }
      setLocal(choice);
      if (pending.current) clearTimeout(pending.current.timer);
      const run = () => {
        pending.current = null;
        inflight.current = api<{ applies: 'now' | 'after_turn' }>(
          `/api/threads/${threadId}`,
          { model: choice.model, effort: choice.effort },
          'PATCH',
        )
          .then((r) => {
            qc.setQueryData<ThreadMeta>(['thread', threadId], (d) => (d ? { ...d, ...choice } : d));
            if (r.applies === 'after_turn') toast('Модель сменится, когда агент закончит текущую работу');
          })
          .catch((e: Error) => {
            setLocal(null);
            toast(e.message);
          });
      };
      if (opts.debounce) pending.current = { timer: setTimeout(run, 450), run };
      else run();
    },
    [threadId, qc],
  );

  /** Дождаться отправки выбора — перед сообщением агенту. */
  const flush = useCallback(async () => {
    if (pending.current) {
      clearTimeout(pending.current.timer);
      pending.current.run();
    }
    await inflight.current;
  }, []);

  return { value, catalog: models.data?.catalog ?? null, change, flush };
}
